import { FunctionComponent, ReactNode } from 'react'
import { X } from 'lucide-react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'

export type ModalSize = 'small' | 'medium' | 'large'

const SIZE_CLASS_MAP: Record<ModalSize, string> = {
  small: 'max-w-modal-small', // confirmations, error dialogs, "are you sure?" prompts, 1-2 form inputs
  medium: 'max-w-modal-medium', // standard forms (2-10 inputs), settings dialogs, moderate text + controls
  large: 'max-w-modal-large' // multi-field forms, dialogs containing tables or lists
}

interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  children?: ReactNode
  actions?: ReactNode
  footnote?: ReactNode
  size?: ModalSize
  disableClose?: boolean
  overflowVisible?: boolean
}

const Modal: FunctionComponent<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  footnote,
  size = 'small',
  disableClose = false,
  overflowVisible = false
}) => (
  <Dialog open={open} as='div' className='relative z105' onClose={disableClose ? () => {} : onClose}>
    <DialogBackdrop transition className='fixed vw-100 vh-100 bottom0 left0 no-pointer-events fade-headless'>
      <div className='w-100 h-100 bg-black o-20' />
    </DialogBackdrop>
    <div className='fixed vw-100 vh-100 bottom0 left0 no-pointer-events'>
      <div className='flex h-100 items-center justify-center p4 max-w-90vw mhauto overflow-y-auto pointer-events-auto'>
        <DialogPanel transition className={`w-100 bg-white shadow-black-16 br3 pv6 fade-headless ${SIZE_CLASS_MAP[size]}`}>
          <div className='ph6 pb4 flex items-start justify-between'>
            <DialogTitle as='h2' className='f3 b black ma0 mr4'>{title}</DialogTitle>
            {!disableClose && (
              <button
                aria-label='close'
                onClick={onClose}
                className='bg-transparent bn pointer pa1 shrink-0 flex items-center justify-center br2 hover-bg-ghost-white'
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            )}
          </div>
          {children !== undefined && children !== null
            ? (
              <div className={`ph6 pb5 f5 lh-copy black${overflowVisible ? ' overflow-visible' : ''}`}>
                {children}
              </div>
              )
            : null}
          {actions !== undefined && actions !== null
            ? (
              <div className='ph6 pt5 flex justify-end gap3'>
                {actions}
              </div>
              )
            : null}
          {footnote !== undefined && footnote !== null
            ? (
              <div className='ph8 pt6 i f6 dim-grey lh-copy'>
                {footnote}
              </div>
              )
            : null}
        </DialogPanel>
      </div>
    </div>
  </Dialog>
)

export default Modal
